import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Shield,
  Lock,
  KeyRound,
  Smartphone,
  FileText,
  Save,
  RotateCcw,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Switch } from '../../components/ui/Switch';
import { Modal } from '../../components/ui/Modal';
import { Skeleton } from '../../components/ui/Skeleton';
import { useThemeStore } from '../../stores/theme.store';
import {
  TenantSecurityConfig,
  UpdateSecurityConfigPayload,
  useResetSecurityConfigDefaults,
  useSecurityConfig,
  useUpdateSecurityConfig,
} from '../../hooks/useSecurityConfig';

// ============================================================
// Form state — local mirror of the config (editable)
// ============================================================

interface FormState {
  // Autenticação
  loginLockEnabled: boolean;
  passwordPolicyEnabled: boolean;
  loginMaxAttempts: number;
  loginLockMinutes: number;
  loginPermanentLockAfter: number;
  passwordMinLength: number;
  passwordRequireUppercase: boolean;
  passwordRequireLowercase: boolean;
  passwordRequireNumber: boolean;
  passwordRequireSpecial: boolean;
  passwordHistorySize: number;
  passwordForceResetOnWeak: boolean;

  // PIX Security
  rateLimitEnabled: boolean;
  pinEnabled: boolean;
  /** Valor em reais (R$) — convertido para centavos no envio */
  pinMinValuePixReais: string;
  /** Valor em reais (R$) — convertido para centavos no envio */
  pinMinValueBoletoReais: string;
  pinMaxAttempts: number;
  pinLockMinutes: number;
  pixMaxAttempts: number;
  pixAttemptsWindowMinutes: number;
  pixBlockDurationMinutes: number;
  pixCooldownSeconds: number;

  // Dispositivos
  newDeviceDetectionEnabled: boolean;
  newDeviceEmailRequired: boolean;
  newDeviceBlockUntilConfirmed: boolean;

  // Auditoria
  transactionAuditEnabled: boolean;
  transactionNotificationEnabled: boolean;
}

function centsToReais(cents: string | number): string {
  const n = typeof cents === 'string' ? Number(cents) : cents;
  if (!Number.isFinite(n)) return '0,00';
  return (n / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function reaisToCents(reais: string): number {
  if (!reais) return 0;
  // Aceita "1.234,56" ou "1234.56" ou "1234,56"
  const normalized = reais.trim().replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function configToForm(config: TenantSecurityConfig): FormState {
  return {
    loginLockEnabled: config.loginLockEnabled,
    passwordPolicyEnabled: config.passwordPolicyEnabled,
    loginMaxAttempts: config.loginMaxAttempts,
    loginLockMinutes: config.loginLockMinutes,
    loginPermanentLockAfter: config.loginPermanentLockAfter,
    passwordMinLength: config.passwordMinLength,
    passwordRequireUppercase: config.passwordRequireUppercase,
    passwordRequireLowercase: config.passwordRequireLowercase,
    passwordRequireNumber: config.passwordRequireNumber,
    passwordRequireSpecial: config.passwordRequireSpecial,
    passwordHistorySize: config.passwordHistorySize,
    passwordForceResetOnWeak: config.passwordForceResetOnWeak,

    rateLimitEnabled: config.rateLimitEnabled,
    pinEnabled: config.pinEnabled,
    pinMinValuePixReais: centsToReais(config.pinMinValuePix),
    pinMinValueBoletoReais: centsToReais(config.pinMinValueBoleto),
    pinMaxAttempts: config.pinMaxAttempts,
    pinLockMinutes: config.pinLockMinutes,
    pixMaxAttempts: config.pixMaxAttempts,
    pixAttemptsWindowMinutes: config.pixAttemptsWindowMinutes,
    pixBlockDurationMinutes: config.pixBlockDurationMinutes,
    pixCooldownSeconds: config.pixCooldownSeconds,

    newDeviceDetectionEnabled: config.newDeviceDetectionEnabled,
    newDeviceEmailRequired: config.newDeviceEmailRequired,
    newDeviceBlockUntilConfirmed: config.newDeviceBlockUntilConfirmed,

    transactionAuditEnabled: config.transactionAuditEnabled,
    transactionNotificationEnabled: config.transactionNotificationEnabled,
  };
}

function formToPayload(form: FormState): UpdateSecurityConfigPayload {
  return {
    loginLockEnabled: form.loginLockEnabled,
    passwordPolicyEnabled: form.passwordPolicyEnabled,
    loginMaxAttempts: form.loginMaxAttempts,
    loginLockMinutes: form.loginLockMinutes,
    loginPermanentLockAfter: form.loginPermanentLockAfter,
    passwordMinLength: form.passwordMinLength,
    passwordRequireUppercase: form.passwordRequireUppercase,
    passwordRequireLowercase: form.passwordRequireLowercase,
    passwordRequireNumber: form.passwordRequireNumber,
    passwordRequireSpecial: form.passwordRequireSpecial,
    passwordHistorySize: form.passwordHistorySize,
    passwordForceResetOnWeak: form.passwordForceResetOnWeak,

    rateLimitEnabled: form.rateLimitEnabled,
    pinEnabled: form.pinEnabled,
    pinMinValuePix: reaisToCents(form.pinMinValuePixReais),
    pinMinValueBoleto: reaisToCents(form.pinMinValueBoletoReais),
    pinMaxAttempts: form.pinMaxAttempts,
    pinLockMinutes: form.pinLockMinutes,
    pixMaxAttempts: form.pixMaxAttempts,
    pixAttemptsWindowMinutes: form.pixAttemptsWindowMinutes,
    pixBlockDurationMinutes: form.pixBlockDurationMinutes,
    pixCooldownSeconds: form.pixCooldownSeconds,

    newDeviceDetectionEnabled: form.newDeviceDetectionEnabled,
    newDeviceEmailRequired: form.newDeviceEmailRequired,
    newDeviceBlockUntilConfirmed: form.newDeviceBlockUntilConfirmed,

    transactionAuditEnabled: form.transactionAuditEnabled,
    transactionNotificationEnabled: form.transactionNotificationEnabled,
  };
}

// ============================================================
// Reusable row primitives (dark-mode aware)
// ============================================================

interface FieldRowProps {
  label: string;
  description?: string;
  children: React.ReactNode;
}

function FieldRow({ label, description, children }: FieldRowProps) {
  const { isDarkMode } = useThemeStore();
  const labelClass = isDarkMode ? 'text-surface-200' : 'text-gray-900';
  const descClass = isDarkMode ? 'text-surface-400' : 'text-gray-500';
  return (
    <div className="flex items-center justify-between gap-6 py-3">
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${labelClass}`}>{label}</div>
        {description && <div className={`text-xs mt-0.5 ${descClass}`}>{description}</div>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

interface SectionCardProps {
  title: string;
  description?: string;
  icon: React.ElementType;
  children: React.ReactNode;
}

function SectionCard({ title, description, icon: Icon, children }: SectionCardProps) {
  const { isDarkMode } = useThemeStore();
  const titleClass = isDarkMode ? 'text-surface-100' : 'text-gray-900';
  const descClass = isDarkMode ? 'text-surface-400' : 'text-gray-500';
  const dividerClass = isDarkMode ? 'divide-surface-800' : 'divide-gray-200';
  return (
    <Card className="p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 rounded-lg bg-primary-500/10 text-primary-400">
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h2 className={`text-lg font-semibold ${titleClass}`}>{title}</h2>
          {description && <p className={`text-sm mt-0.5 ${descClass}`}>{description}</p>}
        </div>
      </div>
      <div className={`divide-y ${dividerClass}`}>{children}</div>
    </Card>
  );
}

// ============================================================
// Loading skeleton
// ============================================================

function LoadingSkeleton() {
  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Skeleton className="h-10 w-40" />
      </div>
      {[1, 2, 3, 4].map((i) => (
        <Card key={i} className="p-6 space-y-4">
          <Skeleton className="h-6 w-48" />
          {[1, 2, 3].map((j) => (
            <div key={j} className="flex items-center justify-between py-2">
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-6 w-11 rounded-full" />
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
}

// ============================================================
// Main page
// ============================================================

export function SecurityConfigPage() {
  const { isDarkMode } = useThemeStore();
  const { data: config, isLoading, error } = useSecurityConfig();
  const updateMutation = useUpdateSecurityConfig();
  const resetMutation = useResetSecurityConfigDefaults();

  const [form, setForm] = useState<FormState | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Sync form with server data (initial load + after save/reset)
  useEffect(() => {
    if (config) {
      setForm(configToForm(config));
    }
  }, [config]);

  const hasChanges = useMemo(() => {
    if (!config || !form) return false;
    const serverForm = configToForm(config);
    return JSON.stringify(serverForm) !== JSON.stringify(form);
  }, [config, form]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSave = async () => {
    if (!form) return;
    try {
      await updateMutation.mutateAsync(formToPayload(form));
      showToast('success', 'Configuração salva com sucesso');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar configuração';
      showToast('error', msg);
    }
  };

  const handleReset = async () => {
    try {
      await resetMutation.mutateAsync();
      setShowResetModal(false);
      showToast('success', 'Configuração restaurada para os valores padrão');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao restaurar configuração';
      showToast('error', msg);
    }
  };

  const handleDiscard = () => {
    if (config) setForm(configToForm(config));
  };

  // Colors
  const titleClass = isDarkMode ? 'text-surface-100' : 'text-gray-900';
  const subtitleClass = isDarkMode ? 'text-surface-400' : 'text-gray-500';

  if (isLoading || !form) return <LoadingSkeleton />;

  if (error) {
    return (
      <div className="p-6">
        <Card className="p-12 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className={`text-lg font-medium ${titleClass} mb-2`}>Erro ao carregar</h3>
          <p className={subtitleClass}>Não foi possível carregar a configuração de segurança.</p>
        </Card>
      </div>
    );
  }

  const ActionButtons = (
    <div className="flex items-center gap-2">
      {hasChanges && (
        <Button variant="ghost" size="sm" onClick={handleDiscard}>
          Descartar
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowResetModal(true)}
        className="flex items-center gap-2"
      >
        <RotateCcw className="w-4 h-4" /> Restaurar padrões
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={handleSave}
        disabled={!hasChanges || updateMutation.isPending}
        isLoading={updateMutation.isPending}
        className="flex items-center gap-2"
      >
        <Save className="w-4 h-4" /> Salvar
      </Button>
    </div>
  );

  return (
    <div className="space-y-6 p-6">
      {/* Toast */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{toast.message}</span>
        </motion.div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${titleClass} flex items-center gap-3`}>
            <Shield className="w-7 h-7 text-primary-400" />
            Configurações de Segurança
          </h1>
          <p className={`${subtitleClass} mt-1`}>
            Controle autenticação, rate limit de PIX, dispositivos e auditoria do tenant.
          </p>
        </div>
        {ActionButtons}
      </div>

      {/* Warning banner */}
      <Card className="p-4 border-amber-500/40 bg-amber-500/5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <h3 className={`font-semibold ${titleClass}`}>Atenção</h3>
            <p className={`text-sm ${subtitleClass} mt-0.5`}>
              Esta configuração afeta todos os correntistas do tenant. Teste com cuidado antes
              de ativar políticas mais restritivas.
            </p>
          </div>
        </div>
      </Card>

      {/* Unsaved changes banner (top) */}
      {hasChanges && (
        <Card className="p-3 border-primary-500/40 bg-primary-500/5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className={`text-sm ${titleClass}`}>Alterações não salvas</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleDiscard}>
                Descartar
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                isLoading={updateMutation.isPending}
              >
                Salvar
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* 1. Autenticação */}
      <SectionCard
        title="Autenticação"
        description="Políticas de bloqueio de login e regras de senha."
        icon={Lock}
      >
        <FieldRow
          label="Bloqueio de conta após tentativas"
          description="Bloqueia o usuário temporariamente quando excede as tentativas de login."
        >
          <Switch
            checked={form.loginLockEnabled}
            onCheckedChange={(v) => set('loginLockEnabled', v)}
          />
        </FieldRow>
        <FieldRow
          label="Máximo de tentativas de login"
          description="Número de tentativas antes do bloqueio temporário."
        >
          <div className="w-32">
            <Input
              type="number"
              min={1}
              value={form.loginMaxAttempts}
              onChange={(e) => set('loginMaxAttempts', Number(e.target.value))}
              disabled={!form.loginLockEnabled}
            />
          </div>
        </FieldRow>
        <FieldRow
          label="Duração do bloqueio (minutos)"
          description="Tempo em que o usuário fica bloqueado após exceder tentativas."
        >
          <div className="w-32">
            <Input
              type="number"
              min={1}
              value={form.loginLockMinutes}
              onChange={(e) => set('loginLockMinutes', Number(e.target.value))}
              disabled={!form.loginLockEnabled}
            />
          </div>
        </FieldRow>
        <FieldRow
          label="Bloqueio permanente após"
          description="Número total de tentativas que dispara bloqueio permanente."
        >
          <div className="w-32">
            <Input
              type="number"
              min={1}
              value={form.loginPermanentLockAfter}
              onChange={(e) => set('loginPermanentLockAfter', Number(e.target.value))}
              disabled={!form.loginLockEnabled}
            />
          </div>
        </FieldRow>

        <FieldRow
          label="Política de senha forte"
          description="Aplica regras de complexidade na criação e troca de senhas."
        >
          <Switch
            checked={form.passwordPolicyEnabled}
            onCheckedChange={(v) => set('passwordPolicyEnabled', v)}
          />
        </FieldRow>
        <FieldRow
          label="Tamanho mínimo da senha"
          description="Mínimo de caracteres exigidos (6–128)."
        >
          <div className="w-32">
            <Input
              type="number"
              min={6}
              max={128}
              value={form.passwordMinLength}
              onChange={(e) => set('passwordMinLength', Number(e.target.value))}
              disabled={!form.passwordPolicyEnabled}
            />
          </div>
        </FieldRow>
        <FieldRow label="Exigir letra maiúscula">
          <Switch
            checked={form.passwordRequireUppercase}
            onCheckedChange={(v) => set('passwordRequireUppercase', v)}
            disabled={!form.passwordPolicyEnabled}
          />
        </FieldRow>
        <FieldRow label="Exigir letra minúscula">
          <Switch
            checked={form.passwordRequireLowercase}
            onCheckedChange={(v) => set('passwordRequireLowercase', v)}
            disabled={!form.passwordPolicyEnabled}
          />
        </FieldRow>
        <FieldRow label="Exigir número">
          <Switch
            checked={form.passwordRequireNumber}
            onCheckedChange={(v) => set('passwordRequireNumber', v)}
            disabled={!form.passwordPolicyEnabled}
          />
        </FieldRow>
        <FieldRow label="Exigir caractere especial">
          <Switch
            checked={form.passwordRequireSpecial}
            onCheckedChange={(v) => set('passwordRequireSpecial', v)}
            disabled={!form.passwordPolicyEnabled}
          />
        </FieldRow>
        <FieldRow
          label="Histórico de senhas"
          description="Quantidade de senhas antigas lembradas para impedir reutilização (0–20)."
        >
          <div className="w-32">
            <Input
              type="number"
              min={0}
              max={20}
              value={form.passwordHistorySize}
              onChange={(e) => set('passwordHistorySize', Number(e.target.value))}
              disabled={!form.passwordPolicyEnabled}
            />
          </div>
        </FieldRow>
        <FieldRow
          label="Forçar troca de senhas fracas"
          description="Exige que usuários com senha fraca troquem no próximo login."
        >
          <Switch
            checked={form.passwordForceResetOnWeak}
            onCheckedChange={(v) => set('passwordForceResetOnWeak', v)}
            disabled={!form.passwordPolicyEnabled}
          />
        </FieldRow>
      </SectionCard>

      {/* 2. PIX Security */}
      <SectionCard
        title="PIX Security"
        description="PIN transacional e rate limit para operações PIX."
        icon={KeyRound}
      >
        <FieldRow
          label="Rate limit de PIX"
          description="Limita tentativas de PIX em uma janela de tempo."
        >
          <Switch
            checked={form.rateLimitEnabled}
            onCheckedChange={(v) => set('rateLimitEnabled', v)}
          />
        </FieldRow>
        <FieldRow
          label="Máximo de tentativas de PIX na janela"
          description="Tentativas permitidas dentro da janela de tempo."
        >
          <div className="w-32">
            <Input
              type="number"
              min={1}
              value={form.pixMaxAttempts}
              onChange={(e) => set('pixMaxAttempts', Number(e.target.value))}
              disabled={!form.rateLimitEnabled}
            />
          </div>
        </FieldRow>
        <FieldRow
          label="Tamanho da janela (minutos)"
          description="Janela deslizante usada para contar tentativas."
        >
          <div className="w-32">
            <Input
              type="number"
              min={1}
              value={form.pixAttemptsWindowMinutes}
              onChange={(e) => set('pixAttemptsWindowMinutes', Number(e.target.value))}
              disabled={!form.rateLimitEnabled}
            />
          </div>
        </FieldRow>
        <FieldRow
          label="Duração do bloqueio (minutos)"
          description="Quanto tempo o usuário fica bloqueado após exceder."
        >
          <div className="w-32">
            <Input
              type="number"
              min={1}
              value={form.pixBlockDurationMinutes}
              onChange={(e) => set('pixBlockDurationMinutes', Number(e.target.value))}
              disabled={!form.rateLimitEnabled}
            />
          </div>
        </FieldRow>
        <FieldRow
          label="Cooldown entre PIX (segundos)"
          description="Tempo mínimo entre operações PIX consecutivas. 0 = desativado."
        >
          <div className="w-32">
            <Input
              type="number"
              min={0}
              value={form.pixCooldownSeconds}
              onChange={(e) => set('pixCooldownSeconds', Number(e.target.value))}
              disabled={!form.rateLimitEnabled}
            />
          </div>
        </FieldRow>

        <FieldRow
          label="PIN transacional"
          description="Exige PIN numérico para transações acima do valor mínimo."
        >
          <Switch checked={form.pinEnabled} onCheckedChange={(v) => set('pinEnabled', v)} />
        </FieldRow>
        <FieldRow
          label="Valor mínimo para PIN em PIX"
          description="Transações PIX acima deste valor exigem PIN."
        >
          <div className="w-40">
            <Input
              leftIcon={<span className="text-surface-400 text-sm">R$</span>}
              inputMode="decimal"
              value={form.pinMinValuePixReais}
              onChange={(e) => set('pinMinValuePixReais', e.target.value)}
              disabled={!form.pinEnabled}
              placeholder="0,00"
            />
          </div>
        </FieldRow>
        <FieldRow
          label="Valor mínimo para PIN em Boleto"
          description="Pagamentos de boleto acima deste valor exigem PIN."
        >
          <div className="w-40">
            <Input
              leftIcon={<span className="text-surface-400 text-sm">R$</span>}
              inputMode="decimal"
              value={form.pinMinValueBoletoReais}
              onChange={(e) => set('pinMinValueBoletoReais', e.target.value)}
              disabled={!form.pinEnabled}
              placeholder="0,00"
            />
          </div>
        </FieldRow>
        <FieldRow
          label="Máximo de tentativas de PIN"
          description="Tentativas antes de bloquear o PIN (1–10)."
        >
          <div className="w-32">
            <Input
              type="number"
              min={1}
              max={10}
              value={form.pinMaxAttempts}
              onChange={(e) => set('pinMaxAttempts', Number(e.target.value))}
              disabled={!form.pinEnabled}
            />
          </div>
        </FieldRow>
        <FieldRow
          label="Duração do bloqueio do PIN (minutos)"
          description="Tempo de bloqueio após exceder tentativas de PIN."
        >
          <div className="w-32">
            <Input
              type="number"
              min={1}
              value={form.pinLockMinutes}
              onChange={(e) => set('pinLockMinutes', Number(e.target.value))}
              disabled={!form.pinEnabled}
            />
          </div>
        </FieldRow>
      </SectionCard>

      {/* 3. Dispositivos */}
      <SectionCard
        title="Dispositivos"
        description="Detecção e controle de novos dispositivos."
        icon={Smartphone}
      >
        <FieldRow
          label="Detecção de novo dispositivo"
          description="Registra e identifica novos dispositivos usados por correntistas."
        >
          <Switch
            checked={form.newDeviceDetectionEnabled}
            onCheckedChange={(v) => set('newDeviceDetectionEnabled', v)}
          />
        </FieldRow>
        <FieldRow
          label="Notificar novo dispositivo por email"
          description="Envia email ao correntista quando um dispositivo novo é detectado."
        >
          <Switch
            checked={form.newDeviceEmailRequired}
            onCheckedChange={(v) => set('newDeviceEmailRequired', v)}
            disabled={!form.newDeviceDetectionEnabled}
          />
        </FieldRow>
        <FieldRow
          label="Bloquear até confirmação"
          description="Bloqueia novo dispositivo até confirmação por email."
        >
          <Switch
            checked={form.newDeviceBlockUntilConfirmed}
            onCheckedChange={(v) => set('newDeviceBlockUntilConfirmed', v)}
            disabled={!form.newDeviceDetectionEnabled}
          />
        </FieldRow>
      </SectionCard>

      {/* 4. Auditoria */}
      <SectionCard
        title="Auditoria"
        description="Registros e notificações de transações."
        icon={FileText}
      >
        <FieldRow
          label="Log de auditoria de transações"
          description="Registra contexto (IP, device, geolocalização) de cada transação."
        >
          <Switch
            checked={form.transactionAuditEnabled}
            onCheckedChange={(v) => set('transactionAuditEnabled', v)}
          />
        </FieldRow>
        <FieldRow
          label="Notificação de transação por email"
          description="Envia email ao correntista quando uma transação é concluída."
        >
          <Switch
            checked={form.transactionNotificationEnabled}
            onCheckedChange={(v) => set('transactionNotificationEnabled', v)}
          />
        </FieldRow>
      </SectionCard>

      {/* Info footer */}
      <Card className="p-4 border-primary-500/30">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-primary-400 mt-0.5 shrink-0" />
          <p className={`text-sm ${subtitleClass}`}>
            Valores BigInt (PIN em PIX/Boleto) são armazenados em centavos no banco e exibidos
            em reais nesta tela. O histórico de alterações fica registrado no audit log do
            tenant.
          </p>
        </div>
      </Card>

      {/* Bottom actions */}
      <div className="flex justify-end">{ActionButtons}</div>

      {/* Floating banner when there are changes */}
      {hasChanges && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40"
        >
          <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-surface-800 border border-primary-500/40 shadow-lg">
            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-sm text-surface-200">Alterações não salvas</span>
            <Button variant="ghost" size="sm" onClick={handleDiscard}>
              Descartar
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              isLoading={updateMutation.isPending}
            >
              Salvar
            </Button>
          </div>
        </motion.div>
      )}

      {/* Reset confirmation modal */}
      <Modal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="Restaurar configuração padrão"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-surface-300">
            Tem certeza que deseja restaurar a configuração de segurança para os valores
            padrão? Todas as personalizações atuais do tenant serão perdidas.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowResetModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleReset}
              isLoading={resetMutation.isPending}
              className="flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" /> Restaurar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default SecurityConfigPage;
